import React from "react";
import {
  Bell,
  Clock,
  AlertTriangle,
  DollarSign,
  Wallet,
  ClipboardList,
  Calendar,
  MessageSquare,
  Megaphone,
  CheckCircle2,
  XCircle,
  Check,
  X,
} from "lucide-react";

const TYPE_META = {
  LATE_CHECKIN: { icon: Clock, tone: "warning" },
  VACANT_CLASS: { icon: AlertTriangle, tone: "danger" },
  FEE_APPROVAL: { icon: DollarSign, tone: "warning" },
  PAYROLL_APPROVAL: { icon: Wallet, tone: "warning" },
  MARKS_SUBMITTED: { icon: ClipboardList, tone: "info" },
  LEAVE_REQUEST: { icon: Calendar, tone: "info" },
  LEAVE_APPROVED: { icon: CheckCircle2, tone: "success" },
  LEAVE_REJECTED: { icon: XCircle, tone: "danger" },
  NOTICE_PUBLISHED: { icon: Megaphone, tone: "info" },
  NEW_MESSAGE: { icon: MessageSquare, tone: "info" },
};

const toneDot = {
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  info: "bg-primary-500",
};

const toneIcon = {
  success: "text-success-600",
  warning: "text-warning-600",
  danger: "text-danger-600",
  info: "text-primary-600 dark:text-primary-400",
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * Single notification row for dropdown or full list.
 */
export default function NotificationItem({
  notification,
  onClick,
  onMarkAsRead,
  onDelete,
  compact = true,
}) {
  const n = notification || {};
  const meta = TYPE_META[n.type] || { icon: Bell, tone: "info" };
  const Icon = meta.icon;
  const tone = meta.tone;
  const unread = n.isRead === false;

  return (
    <li
      className={`flex gap-2.5 sm:gap-3 border-b border-neutral-50 dark:border-neutral-800 px-3 sm:px-4 py-2.5 sm:py-3 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer ${
        unread ? "bg-primary-50/40 dark:bg-primary-500/5" : ""
      }`}
      onClick={() => onClick?.(n)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(n);
        }
      }}
    >
      <span className="mt-0.5 shrink-0">
        <span
          className={`inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 ${toneIcon[tone]}`}
        >
          <Icon size={14} className="sm:hidden" />
          <Icon size={15} className="hidden sm:block" />
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs sm:text-body-md font-semibold text-neutral-900 dark:text-neutral-100 line-clamp-1">
            {n.title || "Notification"}
          </p>
          {unread && (
            <span
              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${toneDot[tone]}`}
              aria-label="Unread"
            />
          )}
        </div>
        <p
          className={`text-xs sm:text-body-sm text-neutral-500 dark:text-neutral-400 ${
            compact ? "truncate" : "line-clamp-3"
          }`}
        >
          {n.message || ""}
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
          <p className="text-[10px] sm:text-label-sm text-neutral-400 dark:text-neutral-500">
            {formatRelativeTime(n.createdAt)}
          </p>

          <div className="flex items-center gap-1.5">
            {unread && onMarkAsRead && (
              <span
                role="button"
                tabIndex={0}
                title="Mark as read"
                aria-label="Mark as read"
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 hover:bg-primary-100 dark:bg-primary-500/15 dark:text-primary-400 dark:hover:bg-primary-500/25"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(n);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onMarkAsRead(n);
                  }
                }}
              >
                <Check size={11} />
                <span className="hidden xs:inline sm:inline">Read</span>
              </span>
            )}

            {onDelete && (
              <span
                role="button"
                tabIndex={0}
                title="Dismiss"
                aria-label="Dismiss"
                className="inline-flex items-center gap-1 rounded-full bg-danger-50 px-2 py-0.5 text-[10px] font-medium text-danger-600 hover:bg-danger-100 dark:bg-danger-500/15 dark:text-danger-500 dark:hover:bg-danger-500/25"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(n);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(n);
                  }
                }}
              >
                <X size={11} />
                <span className="hidden xs:inline sm:inline">Dismiss</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}